import type {
  TestPlayerDeactivateResult,
  TestPlayersDeactivateResponse,
} from '../../lib/api/operatorConsole';

export interface DeactivationReport {
  /** Marks the backend actually lifted. */
  deactivated: number;
  /** Marks it refused to lift because we placed them. */
  refused: number;
  /** Rows it could not act on: unknown id, already inactive, unreadable. */
  unresolved: number;
  /** Every submitted id with the outcome the backend reported for it. */
  rows: TestPlayerDeactivateResult[];
  /**
   * True when the backend lifted at least one mark and reported nothing else.
   * Only then may the screen claim a plain success — a response that lifted
   * nothing is what this whole report exists to show.
   */
  clean: boolean;
}

function countOf(results: TestPlayerDeactivateResult[], status: string): number {
  return results.filter((entry) => entry.status === status).length;
}

// Turns the deactivation envelope into what the screen states. A 2xx says the
// request was processed, not that anything was lifted, so the counts come from
// the response: `summary` when the backend sent that count, `results`
// otherwise.
export function buildDeactivationReport(
  response: TestPlayersDeactivateResponse | undefined,
): DeactivationReport {
  const summary = response?.summary;
  const results = response?.results ?? [];

  const deactivated = summary?.deactivated ?? countOf(results, 'deactivated');
  const refused = summary?.forbidden ?? countOf(results, 'forbidden');
  const unresolved =
    (summary?.not_present ?? countOf(results, 'not_present')) +
    (summary?.already_inactive ?? countOf(results, 'already_inactive')) +
    (summary?.invalid ?? countOf(results, 'invalid'));

  return {
    deactivated,
    refused,
    unresolved,
    rows: results,
    clean: deactivated > 0 && refused === 0 && unresolved === 0,
  };
}
